import { ShareConsiderationAllocationMethod } from 'further-types/exit';
import { PreviousExitDetails } from '../types';
import { getCompanyIdFromPrevious } from './get-company-from-previous';
import { getShareClassIdFromPrevious } from './get-share-class-from-previous';

/**
 * Builds form reset values from previousExitDetails
 * Used in both useEffect hooks to avoid duplication
 */
export const buildFormResetValues = (
  previousExitDetails: PreviousExitDetails | undefined,
  isEditMode: boolean,
  usersFirmId?: string,
) => {
  if (!previousExitDetails || !isEditMode) {
    return {
      firmId: usersFirmId || '',
      firmName: '',
      exitDate: new Date(),
      selectedCompany: { _id: '', legalName: '', tradingName: '' },
      sharesToBeSold: 0,
      sharePrice: '0',
      taxableValueSharePrice: '0',
      taxableValueTotal: '0',
      receivedValueTotal: '0',
      isReceivedValueSharePriceEdited: false,
      chargeAccruedFees: undefined,
      taxReliefElegible: undefined,
      selectedShareClasses: [],
      selectedAllocations: [],
      shareAllocationUploadIds: [],
      taxReliefElegibleAllocationIds: [],
      considerationType: undefined,
      shareConsiderationCompanyId: undefined,
      shareConsiderationShareClassId: undefined,
      totalConsiderationShares: '0',
      shareConsiderationValuePerShare: '0',
      shareConsiderationValueTotal: '0',
      shareConsiderationEisEligible: false,
      shareConsiderationAllocationMethod: undefined,
      permitPartialShareAllocation: false,
    };
  }

  const companyId = getCompanyIdFromPrevious(previousExitDetails, isEditMode);
  const shareClassId = getShareClassIdFromPrevious(
    previousExitDetails,
    isEditMode,
  );

  return {
    firmId: previousExitDetails.firmId || '',
    firmName: previousExitDetails.firmName || '',
    exitDate: previousExitDetails.exitDate
      ? previousExitDetails.exitDate instanceof Date
        ? previousExitDetails.exitDate
        : new Date(previousExitDetails.exitDate)
      : new Date(),
    selectedCompany: {
      _id: companyId || '',
      legalName: previousExitDetails.selectedCompany?.legalName,
      tradingName: previousExitDetails.selectedCompany?.tradingName,
    },
    sharesToBeSold: previousExitDetails.sharesToBeSold || 0,
    sharePrice: previousExitDetails.sharePrice?.toString() || '0',
    taxableValueSharePrice:
      previousExitDetails.taxableValueSharePrice?.toString() || '0',
    taxableValueTotal: previousExitDetails.taxableValueTotal || '0',
    receivedValueTotal: previousExitDetails.receivedValueTotal || '0',
    isReceivedValueSharePriceEdited: false,
    chargeAccruedFees: previousExitDetails.chargeAccruedFees,
    taxReliefElegible: previousExitDetails.taxReliefElegible,
    selectedShareClasses: previousExitDetails.selectedShareClasses || [],
    selectedAllocations: previousExitDetails.shareAllocationUploadIds || [],
    shareAllocationUploadIds:
      previousExitDetails.shareAllocationUploadIds || [],
    taxReliefElegibleAllocationIds:
      previousExitDetails.taxReliefElegibleAllocationIds || [],
    considerationType: previousExitDetails.considerationType,
    shareConsiderationCompanyId:
      previousExitDetails.shareConsiderationCompanyId,
    shareConsiderationShareClassId: shareClassId,
    totalConsiderationShares:
      previousExitDetails.totalConsiderationShares?.toString() || '0',
    shareConsiderationValuePerShare:
      previousExitDetails.shareConsiderationValuePerShare?.toString() || '0',
    shareConsiderationValueTotal:
      previousExitDetails.shareConsiderationValueTotal?.toString() || '0',
    shareConsiderationEisEligible:
      previousExitDetails.shareConsiderationEisEligible,
    shareConsiderationAllocationMethod:
      previousExitDetails.shareConsiderationAllocationMethod ===
        ShareConsiderationAllocationMethod.Upload ||
      previousExitDetails.shareConsiderationAllocationMethod ===
        ShareConsiderationAllocationMethod.Automatic
        ? previousExitDetails.shareConsiderationAllocationMethod
        : undefined,
    permitPartialShareAllocation:
      previousExitDetails.permitPartialShareAllocation ?? false,
  };
};
